import ApartmentIcon from '@mui/icons-material/Apartment';
import GroupIcon from '@mui/icons-material/Group';
import ImageIcon from '@mui/icons-material/Image';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import PersonIcon from '@mui/icons-material/Person';
import PlaceIcon from '@mui/icons-material/Place';
import { Avatar, AvatarProps, Badge, Skeleton, Stack, Tooltip } from '@mui/material';
import { ReactNode } from 'react';

import getActivityColor from '../../Utils/getActivityColor';
import { UploadType } from '../Image/UploadType';

interface AvatarImageWrapperProps extends AvatarProps {
    busy?: boolean;
    hasTooltip?: boolean;
    height?: number;
    imagePath: string | undefined;
    imageType: UploadType | null;
    isLoading: boolean;
    lastSeenMinutes?: number | null;
    minBadgeSize?: number;
    tooltipContent?: string | ReactNode;
    type?: 'default' | 'activityBadge' | 'activityCircular' | 'activityCircularBadge';
    width?: number;
}

const resolvePath = (imagePath: AvatarImageWrapperProps['imagePath']): string | null => {
    const basePath = process.env.EXPO_PUBLIC_IMAGE_SERVE_HOST!;
    if (!imagePath || imagePath === '') {
        return null;
    }

    // If already an absolute URL, return as-is (still ensure no duplicate .webp append)
    if (/^https?:\/\//i.test(imagePath)) {
        return imagePath;
    }

    // Check if the filename already has an extension (e.g., .png, .jpg, .jpeg, .webp, .gif, .svg)
    const hasKnownExtension = /\.[a-z0-9]{2,5}$/i.test(imagePath);

    return hasKnownExtension ? `${basePath}/${imagePath}` : `${basePath}/${imagePath}.webp`;
};

const getFallbackIcon = (imageType: UploadType | null) => {
    const iconStyle = {
        color: '#1C4E71',
        filter: 'drop-shadow(0px 2px 3px rgba(0, 0, 0, 0.25))',
        height: '75%',
        width: '100%',
    };

    const IconComponent = (() => {
        switch (imageType) {
            case UploadType.Profile:
                return PersonIcon;
            case UploadType.Company:
                return ApartmentIcon;
            case UploadType.Site:
                return PlaceIcon;
            case UploadType.Instance:
                return ImageIcon;
            case UploadType.Group:
                return GroupIcon;
            case UploadType.Room:
                return MeetingRoomIcon;
            default:
                return PersonIcon;
        }
    })();

    return (
        <Stack
            sx={{
                alignItems: 'center',
                bgcolor: 'white',
                display: 'flex',
                flex: 1,
                height: '100%',
                justifyContent: 'center',
                width: '100%',
            }}
        >
            <IconComponent sx={iconStyle} />
        </Stack>
    );
};

function AvatarImageWrapper(props: Readonly<AvatarImageWrapperProps>) {
    const {
        busy,
        hasTooltip = false,
        height = 46,
        imagePath,
        imageType,
        isLoading,
        lastSeenMinutes,
        minBadgeSize = 14,
        tooltipContent = '',
        type = 'default',
        variant,
        width = 46,
        ...rest
    } = props;

    const activityColor = getActivityColor({ busy, lastSeenMinutes });
    const isSquare = variant === 'square';
    const resolvedPath = resolvePath(imagePath);

    // Calculate badge size proportionally
    const badgeSizer = width ? width / 70 : 1;
    const badgeSize = Math.max(14 * badgeSizer, minBadgeSize);
    const borderWidth = width ? width / 22 : 2;

    // Common style objects
    const avatarStyles = {
        bgcolor: !resolvedPath ? 'white' : undefined,
        border: type === 'default' ? '2px solid #ddd' : 'initial',
        height,
        width,
    };

    const avatarBorderStyles = {
        bgcolor: !resolvedPath ? 'white' : undefined,
        borderColor: activityColor,
        borderStyle: 'solid',
        borderWidth,
        height,
        width,
    };

    const badgeStyles = {
        '& .MuiBadge-badge': {
            backgroundColor: activityColor,
            borderRadius: '50%',
            boxShadow: '0 0 0 1px #adadb8',
            height: badgeSize,
            width: badgeSize,
        },
    };

    if (isLoading) {
        return <Skeleton height={height ?? '100%'} variant={isSquare ? 'rectangular' : 'circular'} width={width} />;
    }

    const renderAvatar = () => {
        switch (type) {
            case 'default':
                return (
                    <Avatar
                        src={resolvedPath || undefined}
                        sx={avatarStyles}
                        variant={variant}
                        {...rest}
                        onDragStart={(e) => e.preventDefault()}
                    >
                        {getFallbackIcon(imageType)}
                    </Avatar>
                );
            case 'activityBadge':
                return (
                    <Badge
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        overlap="circular"
                        sx={badgeStyles}
                        variant="dot"
                    >
                        <Avatar
                            src={resolvedPath || undefined}
                            sx={avatarStyles}
                            variant={variant}
                            {...rest}
                            onDragStart={(e) => e.preventDefault()}
                        >
                            {getFallbackIcon(imageType)}
                        </Avatar>
                    </Badge>
                );
            case 'activityCircular':
                return (
                    <Avatar
                        src={resolvedPath || undefined}
                        sx={avatarBorderStyles}
                        variant={variant}
                        {...rest}
                        onDragStart={(e) => e.preventDefault()}
                    >
                        {getFallbackIcon(imageType)}
                    </Avatar>
                );
            case 'activityCircularBadge':
                return (
                    <Badge
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        overlap="circular"
                        sx={badgeStyles}
                        variant="dot"
                    >
                        <Avatar
                            src={resolvedPath || undefined}
                            sx={avatarBorderStyles}
                            variant={variant}
                            {...rest}
                            onDragStart={(e) => e.preventDefault()}
                        >
                            {getFallbackIcon(imageType)}
                        </Avatar>
                    </Badge>
                );
            default:
                return (
                    <Avatar variant={variant} {...rest} onDragStart={(e) => e.preventDefault()}>
                        {getFallbackIcon(imageType)}
                    </Avatar>
                );
        }
    };

    return hasTooltip ? <Tooltip title={tooltipContent}>{renderAvatar()}</Tooltip> : renderAvatar();
}

export default AvatarImageWrapper;
